import { Button } from "components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { zodResolver } from "@hookform/resolvers/zod";
import { toast } from "components/ui/use-toast";
import { Building2, CheckCircle, Mail, Users, Loader2, Store } from "lucide-react";
import { useForm } from "react-hook-form";
import { useAddStoreEmailMutation } from "@/services/conciliation/store-email";
import { useDialogModal } from "@/hook/handle-modal/hooks/actions";
import { storeEmailFormSchema, StoreEmailFormType } from "../schemas/store-email.schema";
import { ICreateBatchStoreEmail, EmailData } from "@/@types/store-email";
import { Badge } from "@/components/ui/badge";

const AddEmailToStore = () => {
  const [addStoreEmail, { isLoading }] = useAddStoreEmailMutation();
  const { handleModal } = useDialogModal();

  // Gerar lista de lojas de 1 a 200
  const storeOptions = Array.from({ length: 200 }, (_, i) => ({
    value: (i + 1).toString(),
    label: `Loja ${(i + 1).toString().padStart(3, '0')}`
  }));

  const form = useForm<StoreEmailFormType>({
    resolver: zodResolver(storeEmailFormSchema),
    defaultValues: {
      storeCode: "",
      managementEmail: "",
      storeEmail: "",
    },
  });

  const onSubmit = async (values: StoreEmailFormType) => {
    try {
      const emails: EmailData[] = [];

      if (values.managementEmail) {
        emails.push({
          email: values.managementEmail,
          type: "MANAGEMENT"
        });
      }

      if (values.storeEmail) {
        emails.push({
          email: values.storeEmail,
          type: "STORE"
        });
      }

      const payload: ICreateBatchStoreEmail = {
        storeCode: values.storeCode,
        emails: emails
      };

      await addStoreEmail(payload).unwrap();

      toast({
        title: "Emails adicionados com sucesso!",
        description: `Os emails foram configurados para a loja ${values.storeCode}.`,
        variant: "default",
      });

      handleModal({ isOpen: false });
      form.reset();
    } catch (error: any) {
      toast({
        title: "Erro ao adicionar emails",
        description: error?.data?.message || "Ocorreu um erro inesperado. Tente novamente.",
        variant: "destructive",
      });
    }
  };

  return (
    <div className="bg-gradient-to-br lg:max-w-xl from-[#613F4F]/5 to-white flex items-center justify-center px-0">
      <Card className="w-full shadow-none border-0 bg-white px-0">
        <CardContent className="border-0 px-0">
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
              <div className="grid gap-6">
                {/* Store Selection */}
                <FormField
                  control={form.control}
                  name="storeCode"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="flex items-center gap-2 text-[#613F4F] font-semibold">
                        <Store className="h-4 w-4" />
                        Selecionar Loja
                        <Badge
                          variant="secondary"
                          className="bg-[#F6D040]/20 text-[#613F4F] border-[#F6D040]/40"
                        >
                          OBRIGATÓRIO
                        </Badge>
                      </FormLabel>
                      <Select onValueChange={field.onChange} defaultValue={field.value}>
                        <FormControl>
                          <SelectTrigger className="h-12 border-[#613F4F]/20 focus:border-[#613F4F] focus:ring-[#613F4F]/20">
                            <SelectValue placeholder="Escolha uma loja..." />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent className="max-h-60">
                          {storeOptions.map((store) => (
                            <SelectItem key={store.value} value={store.value}>
                              {store.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormDescription className="text-sm text-gray-600">
                        Selecione a loja para configurar os emails
                      </FormDescription>
                      <FormMessage className="text-red-600" />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="managementEmail"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="flex items-center gap-2 text-[#613F4F] font-semibold">
                        <Users className="h-4 w-4" />
                        Email da Gerência
                        <Badge
                          variant="secondary"
                          className="bg-[#613F4F]/10 text-[#613F4F] border-[#613F4F]/20"
                        >
                          GERÊNCIA
                        </Badge>
                      </FormLabel>
                      <FormControl>
                        <div className="relative">
                          <Input
                            {...field}
                            type="email"
                            placeholder="gerencia@exemplo.com.br"
                            className="pl-10 border-[#613F4F]/20 focus:border-[#613F4F] focus:ring-[#613F4F]/20 h-12"
                          />
                          <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-[#613F4F]/50" />
                        </div>
                      </FormControl>
                      <FormDescription className="text-sm text-gray-600">
                        Email para comunicações administrativas e de gerência
                      </FormDescription>
                      <FormMessage className="text-red-600" />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="storeEmail"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="flex items-center gap-2 text-[#613F4F] font-semibold">
                        <Building2 className="h-4 w-4" />
                        Email da Loja
                        <Badge
                          variant="secondary"
                          className="bg-blue-50 text-blue-700 border-blue-200"
                        >
                          LOJA
                        </Badge>
                      </FormLabel>
                      <FormControl>
                        <div className="relative">
                          <Input
                            {...field}
                            type="email"
                            placeholder="loja@exemplo.com.br"
                            className="pl-10 border-[#613F4F]/20 focus:border-[#613F4F] focus:ring-[#613F4F]/20 h-12"
                          />
                          <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-[#613F4F]/50" />
                        </div>
                      </FormControl>
                      <FormDescription className="text-sm text-gray-600">
                        Email para operações da loja e comunicações com clientes
                      </FormDescription>
                      <FormMessage className="text-red-600" />
                    </FormItem>
                  )}
                />
              </div>

              <div className="bg-[#F6D040]/10 border border-[#F6D040]/30 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <CheckCircle className="h-5 w-5 text-[#613F4F] mt-0.5" />
                  <div>
                    <h4 className="font-medium text-[#613F4F] mb-1">
                      Informações Importantes
                    </h4>
                    <p className="text-sm text-gray-600">
                      Selecione uma loja e preencha pelo menos um email. Os emails serão
                      associados à loja selecionada para comunicações específicas.
                    </p>
                  </div>
                </div>
              </div>

              <div className="flex gap-3 pt-4 -full items-end justify-end">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => handleModal({ isOpen: false })}
                  className="border-[#613F4F] text-[#613F4F] hover:bg-[#613F4F]/10"
                  disabled={isLoading}
                >
                  Cancelar
                </Button>
                <Button
                  type="submit"
                  className="bg-[#F6D040] text-[#613F4F] hover:bg-[#F6D040]/90"
                  disabled={isLoading}
                >
                  {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  {isLoading ? "Adicionando..." : "Adicionar Emails"}
                </Button>
              </div>
            </form>
          </Form>
        </CardContent>
      </Card>
    </div>
  );
};

export default AddEmailToStore;
